<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from 'vue'

import BetaRedirectRow from '@/components/beta-dashboard/molecules/BetaRedirectRow.vue'
import notification, { type ToastId } from '@/services/notification'
import { useBetaDashboardStore } from '@/stores/beta-dashboard'
import type { BetaRedirect } from '@/types/beta-dashboard'
import { normalizeBetaRedirects } from '@/utils/beta-redirects'

const betaStore = useBetaDashboardStore()

const draft = ref<BetaRedirect[]>([])
const baseline = ref('[]')
const saving = ref(false)

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value)) as T

watch(
    () => betaStore.redirects,
    (redirects) => {
        draft.value = clone(redirects)
        baseline.value = JSON.stringify(draft.value)
    },
    { deep: true, immediate: true },
)

onBeforeMount(() => betaStore.loadRedirects())

const dirty = computed(() => JSON.stringify(draft.value) !== baseline.value)

const addRedirect = () => {
    draft.value.push({
        from: { id: null, path: '' },
        to: { id: null, path: '' },
        error: '',
    })
}

const removeRedirect = (index: number) => {
    draft.value.splice(index, 1)
}

const saveRedirects = async () => {
    if (!dirty.value || saving.value) return

    saving.value = true
    const toastId: ToastId = notification.loading('Salvando redirecionamentos…')
    try {
        const normalized = normalizeBetaRedirects(draft.value)
        const saved = await betaStore.updateRedirects(normalized)
        draft.value = clone(saved)
        baseline.value = JSON.stringify(draft.value)
        notification.update(toastId, 'Redirecionamentos salvos com sucesso.', 'success')
    } catch (error) {
        notification.update(
            toastId,
            error instanceof Error ? error.message : 'Não foi possível salvar os redirecionamentos.',
            'error',
        )
    } finally {
        saving.value = false
    }
}
</script>

<template>
    <div class="beta-redirects">
        <div class="beta-redirects__heading">
            <div>
                <p>Redirecionamentos</p>
                <h2>Encaminhe endereços antigos</h2>
                <span>Leve visitantes de uma URL antiga para o novo destino dentro do site.</span>
            </div>
            <button type="button" @click="addRedirect">+ Novo redirecionamento</button>
        </div>

        <aside class="beta-redirects__tip">
            <strong>Como preencher</strong>
            <span>Você pode informar apenas o caminho ou colar a URL completa. O endereço será normalizado ao salvar.</span>
        </aside>

        <div v-if="betaStore.loadingRedirects" class="beta-redirects__state">
            Carregando redirecionamentos…
        </div>

        <div v-else-if="betaStore.redirectsError" class="beta-redirects__state is-error">
            <span>{{ betaStore.redirectsError }}</span>
            <button type="button" @click="betaStore.loadRedirects()">Tentar novamente</button>
        </div>

        <div v-else-if="!draft.length" class="beta-redirects__state">
            <strong>Nenhum redirecionamento cadastrado</strong>
            <span>Crie uma regra para encaminhar um endereço antigo.</span>
            <button type="button" @click="addRedirect">Criar redirecionamento</button>
        </div>

        <div v-else class="beta-redirects__list">
            <BetaRedirectRow
                v-for="(redirect, index) in draft"
                :key="`${redirect.from.id ?? 'new'}-${index}`"
                :redirect="redirect"
                :index="index"
                @update-from="redirect.from.path = $event"
                @update-to="redirect.to.path = $event"
                @remove="removeRedirect(index)"
            />
        </div>

        <div class="beta-redirects__actions">
            <small v-if="dirty">Existem alterações ainda não salvas.</small>
            <button type="button" :disabled="!dirty || saving" @click="saveRedirects">
                {{ saving ? 'Salvando…' : 'Salvar redirecionamentos' }}
            </button>
        </div>
    </div>
</template>

<style scoped>
.beta-redirects {
    display: grid;
    gap: 16px;
}

.beta-redirects__heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 17px;
    border-bottom: 1px solid #edf1f6;
}

.beta-redirects__heading p {
    color: var(--main-color);
    font-size: 10px;
    font-weight: 900;
    letter-spacing: 0.18em;
    text-transform: uppercase;
}

.beta-redirects__heading h2 {
    margin-top: 3px;
    color: #172d4a;
    font-size: 21px;
    font-weight: 850;
    letter-spacing: -0.025em;
}

.beta-redirects__heading span {
    display: block;
    margin-top: 5px;
    color: #7d8da3;
    font-size: 11px;
}

.beta-redirects__heading > button,
.beta-redirects__actions button,
.beta-redirects__state button {
    min-height: 38px;
    border-radius: var(--beta-button-radius);
    background: var(--main-color);
    padding: 0 14px;
    color: #fff;
    font-size: 10px;
    font-weight: 850;
}

.beta-redirects__tip {
    display: flex;
    align-items: center;
    gap: 10px;
    border: 1px solid color-mix(in srgb, var(--main-color) 15%, #e1e7ef);
    border-radius: 13px;
    background: color-mix(in srgb, var(--main-color) 4%, #f9fbfd);
    padding: 10px 12px;
}

.beta-redirects__tip strong {
    flex: none;
    color: #314965;
    font-size: 12px;
    font-weight: 850;
}

.beta-redirects__tip span {
    color: #7c8ba0;
    font-size: 10px;
    line-height: 1.45;
}

.beta-redirects__list {
    display: grid;
    gap: 8px;
}

.beta-redirects__state {
    display: grid;
    min-height: 170px;
    place-content: center;
    justify-items: center;
    gap: 6px;
    border: 1px dashed #dbe3ed;
    border-radius: 17px;
    background: #f9fbfd;
    padding: 20px;
    color: #7e8da2;
    font-size: 12px;
    text-align: center;
}

.beta-redirects__state strong {
    color: #304863;
    font-size: 14px;
}

.beta-redirects__state button {
    margin-top: 5px;
}

.beta-redirects__state.is-error {
    color: #a34e4e;
}

.beta-redirects__actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 11px;
}

.beta-redirects__actions small {
    color: #8493a7;
    font-size: 10px;
}

.beta-redirects__actions button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
}

@media (max-width: 640px) {
    .beta-redirects__heading {
        flex-direction: column;
    }

    .beta-redirects__tip,
    .beta-redirects__actions {
        align-items: flex-start;
        flex-direction: column;
    }

    .beta-redirects__actions button {
        width: 100%;
    }
}
</style>
